import { sendRequest } from 'best-modules/vue-utils'
import { Query } from 'best-modules/utils'
import {
  DocumentRoute,
  DocumentRouteStage,
  DocumentRouteStageParticipant,
  DocumentTypeRouteStageCredentials,
  DocumentRouteStageParticipantCredentials,
} from '@/utils/types/documentRoute'
import {
  Document,
  DocumentDTO,
  DocumentAcceptance,
  DocumentAcceptanceConfirmation,
  DocumentAcceptanceConfirmationCredentials,
  DocumentAcceptanceCredentials,
  DocumentRouteSignature,
  DocumentRouteSignatureCredentials,
  DocumentVersion,
  DocumentResolution,
  DocumentResolutionCredentials,
  DocumentCounts,
  DocumentListItem,
} from '@/utils/types/document'
import { PaginationList } from 'best-modules/components/server-table/types'
import { ISODate } from 'best-modules/utils/customDate'
import { DefaultItem } from 'best-modules/plugins/directory/types'

const documentList = (
  query: Query
): Promise<PaginationList<DocumentListItem>> => {
  return sendRequest({
    name: 'documentList',
    method: 'get',
    url: {
      url: '/json/doc-type/document',
      query,
    },
  })
}

const documentSingle = (uuid: string): Promise<Document> => {
  return sendRequest({
    name: 'documentSingle',
    method: 'get',
    url: `/json/doc-type/document/single/${uuid}`,
  })
}

const documentCreate = (credentials: {
  docTypeClassId: number
  docTypeDefinitionId: number
}): Promise<Document> => {
  return sendRequest({
    name: 'documentCreate',
    method: 'post',
    url: '/json/doc-type/document/create',
    payload: credentials,
  })
}

const documentUpdate = (
  documentId: number,
  credentials: DocumentDTO
): Promise<Document> => {
  return sendRequest({
    name: 'documentUpdate',
    method: 'post',
    url: `/json/doc-type/document/update/${documentId}`,
    payload: credentials,
  })
}

const documentDelete = (documentId: number): Promise<void> => {
  return sendRequest({
    name: 'documentDelete',
    method: 'delete',
    url: `/json/doc-type/document/delete/${documentId}`,
  })
}

const documentRevision = (
  documentId: number,
  comment: string
): Promise<Document> => {
  return sendRequest({
    name: 'documentRevision',
    method: 'post',
    url: `/json/doc-type/document/revision/${documentId}`,
    payload: { comment },
  })
}

const documentSendToRegistration = (documentId: number): Promise<Document> => {
  return sendRequest({
    name: 'documentSendToRegistration',
    method: 'post',
    url: `/json/doc-type/document/send-to-registration/${documentId}`,
  })
}

const documentRejectRegistration = (documentId: number): Promise<Document> => {
  return sendRequest({
    name: 'documentRejectRegistration',
    method: 'post',
    url: `/json/doc-type/document/reject-registration/${documentId}`,
  })
}

const documentReturnFromRegistration = (
  documentId: number
): Promise<Document> => {
  return sendRequest({
    name: 'documentReturnFromRegistration',
    method: 'post',
    url: `/json/doc-type/document/return-from-registration/${documentId}`,
  })
}

const documentCounts = (): Promise<DocumentCounts> => {
  return sendRequest({
    name: 'documentCounts',
    method: 'get',
    url: '/json/doc-type/document/counts',
  })
}

const documentDashboard = () => {
  return sendRequest({
    name: 'documentDashboard',
    method: 'get',
    url: '/json/document/dashboard',
  })
}

const printedFormDocument = (query: Query): Promise<any> => {
  return sendRequest({
    name: 'printedFormDocument',
    method: 'get',
    url: {
      url: '/json/printedForm/document',
      query,
    },
  })
}

const documentManualRegister = (
  documentId: number,
  payload: {
    registrationJournalId: number
    registrationNumber: string
    registrationDate: ISODate
  }
): Promise<Document> => {
  return sendRequest({
    name: 'documentManualRegister',
    method: 'post',
    url: `/json/doc-type/document/manual-register/${documentId}`,
    payload,
  })
}

const documentSendToReview = (
  documentId: number,
  payload: { reviewerIds: number[] }
): Promise<void> => {
  return sendRequest({
    name: 'documentSendToReview',
    method: 'post',
    url: `/json/doc-type/document/review/send-to-review/${documentId}`,
    payload,
  })
}
const documentReturnFromReview = (
  reviewId: number,
  payload: { comment: string }
): Promise<void> => {
  return sendRequest({
    name: 'documentReturnFromReview',
    method: 'post',
    url: `/json/doc-type/document/review/return-from-review/${reviewId}`,
    payload,
  })
}
const documentChangeReviewer = (
  reviewId: number,
  payload: { comment: string; newReviewerId: number }
): Promise<void> => {
  return sendRequest({
    name: 'documentReturnFromReview',
    method: 'post',
    url: `/json/doc-type/document/review/change-reviewer/${reviewId}`,
    payload,
  })
}

const documentReviewList = (documentId: number): Promise<any[]> => {
  return sendRequest({
    name: 'documentReviewList',
    method: 'get',
    url: `/json/doc-type/document/review/byDocTypeDocument/${documentId}`,
  })
}

const documentActiveRoute = (documentId: number): Promise<DocumentRoute> => {
  return sendRequest({
    name: 'documentActiveRoute',
    method: 'get',
    url: `/json/doc-type/document/route/byDocTypeDocument/${documentId}`,
  })
}

const documentRouteRun = (routeId: number): Promise<DocumentRoute> => {
  return sendRequest({
    name: 'documentRouteRun',
    method: 'post',
    url: '/json/doc-type/document/route/run',
    payload: {
      docTypeDocumentRouteId: routeId,
    },
  })
}

const documentRouteStageCreate = (
  credentials: DocumentTypeRouteStageCredentials
): Promise<DocumentRouteStage> => {
  return sendRequest({
    name: 'documentRouteStageCreate',
    method: 'post',
    url: '/json/doc-type/document/route/stage/create',
    payload: credentials,
  })
}

const documentRouteStageUpdate = (
  stageId: number,
  credentials: DocumentTypeRouteStageCredentials
): Promise<DocumentRouteStage> => {
  return sendRequest({
    name: 'documentRouteStageUpdate',
    method: 'put',
    url: `/json/doc-type/document/route/stage/update/${stageId}`,
    payload: credentials,
  })
}

const documentRouteStageDelete = (stageId: number): Promise<void> => {
  return sendRequest({
    name: 'documentRouteStageDelete',
    method: 'delete',
    url: `/json/doc-type/document/route/stage/delete/${stageId}`,
  })
}

const documentRouteStageParticipantCreate = (
  credentials: DocumentRouteStageParticipantCredentials
): Promise<DocumentRouteStageParticipant> => {
  return sendRequest({
    name: 'documentRouteStageParticipantCreate',
    method: 'post',
    url: '/json/doc-type/document/route/stage/participant/create',
    payload: credentials,
  })
}

const documentRouteStageParticipantDelete = (
  participantId: number
): Promise<void> => {
  return sendRequest({
    name: 'documentRouteStageParticipantDelete',
    method: 'delete',
    url: `/json/doc-type/document/route/stage/participant/delete/${participantId}`,
  })
}

const documentRouteStageParticipantSetResult = (
  participantIds: number[],
  resultId: number
): Promise<any> => {
  return sendRequest({
    name: 'documentRouteStageParticipantSetResult',
    method: 'patch',
    url: '/json/doc-type/document/route/stage/participant/result',
    payload: { resultId, participantIds },
  })
}

const documentSignatureList = (
  documentId: number
): Promise<DocumentRouteSignature[]> => {
  return sendRequest({
    name: 'documentSignatureList',
    method: 'get',
    url: `/json/doc-type/document/signature/byDocTypeDocument/${documentId}`,
  })
}

const documentSignatureCreate = (
  credentials: DocumentRouteSignatureCredentials[]
): Promise<DocumentRouteSignature[]> => {
  return sendRequest({
    name: 'documentSignatureCreate',
    method: 'post',
    url: '/json/doc-type/document/signature/create',
    payload: {
      signatures: credentials,
    },
  })
}

const documentAcceptanceList = (
  documentId: number
): Promise<DocumentAcceptance[]> => {
  return sendRequest({
    name: 'documentAcceptanceList',
    method: 'get',
    url: `/json/doc-type/document/acceptance/byDocTypeDocument/${documentId}`,
  })
}

const documentAcceptanceCreate = (
  credentials: DocumentAcceptanceCredentials
): Promise<DocumentAcceptance> => {
  return sendRequest({
    name: 'documentAcceptanceCreate',
    method: 'post',
    url: '/json/doc-type/document/acceptance/create',
    payload: credentials,
  })
}

const documentAcceptanceUpdate = (
  acceptanceId: number,
  credentials: Omit<
    DocumentAcceptanceCredentials,
    'acceptorTypeId' | 'acceptorId'
  >
): Promise<DocumentAcceptance> => {
  return sendRequest({
    name: 'documentAcceptanceUpdate',
    method: 'put',
    url: `/json/doc-type/document/acceptance/update/${acceptanceId}`,
    payload: credentials,
  })
}

const documentAcceptanceDelete = (acceptanceId: number): Promise<void> => {
  return sendRequest({
    name: 'documentAcceptanceDelete',
    method: 'delete',
    url: `/json/doc-type/document/acceptance/delete/${acceptanceId}`,
  })
}

const documentAcceptanceConfirmationList = (
  documentId: number
): Promise<DocumentAcceptanceConfirmation[]> => {
  return sendRequest({
    name: 'documentAcceptanceConfirmationList',
    method: 'get',
    url: `/json/doc-type/document/acceptance/confirmation/byDocTypeDocument/${documentId}`,
  })
}

const documentAcceptanceConfirmationCreate = (
  credentials: DocumentAcceptanceConfirmationCredentials
): Promise<DocumentAcceptanceConfirmation> => {
  return sendRequest({
    name: 'documentAcceptanceConfirmationCreate',
    method: 'post',
    url: '/json/doc-type/document/acceptance/confirmation/create',
    payload: credentials,
  })
}

const documentAcceptanceStatus = (
  documentId: number
): Promise<{
  isInAcceptanceList: boolean
  acceptanceType: DefaultItem
  isConfirmed: boolean
  confirmedAt: ISODate
}> => {
  return sendRequest({
    name: 'documentAcceptanceStatus',
    method: 'get',
    url: `/json/doc-type/document/acceptance/my-status/${documentId}`,
  })
}

const documentRelationList = (documentId: number): Promise<any> => {
  return sendRequest({
    name: 'documentRelationList',
    method: 'get',
    url: `/json/doc-type/document/relation/byDocTypeDocument/${documentId}`,
  })
}

const documentRelationCreate = (credentials: any): Promise<any> => {
  return sendRequest({
    name: 'documentRelationCreate',
    method: 'post',
    url: '/json/doc-type/document/relation/create',
    payload: credentials,
  })
}

const documentRelationDelete = (
  documentId: number,
  relatedDocumentId: number
): Promise<any> => {
  return sendRequest({
    name: 'documentRelationDelete',
    method: 'delete',
    url: `/json/doc-type/document/relation/delete/${documentId}/${relatedDocumentId}`,
  })
}

const documentHistoryList = (documentId: number): Promise<any[]> => {
  return sendRequest({
    name: 'documentHistoryList',
    method: 'get',
    url: `/json/doc-type/document/${documentId}/history`,
  })
}

const documentVersionList = (
  documentId: number
): Promise<DocumentVersion[]> => {
  return sendRequest({
    name: 'documentVersionList',
    method: 'get',
    url: `/json/doc-type/document/version/byDocTypeDocument/${documentId}`,
  })
}

const documentVersionRollback = (
  versionId: number
): Promise<DocumentVersion> => {
  return sendRequest({
    name: 'documentVersionRollback',
    method: 'post',
    url: `/json/doc-type/document/version/rollback/${versionId}`,
    payload: { docTypeDocumentVersionId: versionId },
  })
}

const documentResolutionList = (
  documentId: number
): Promise<DocumentResolution[]> => {
  return sendRequest({
    name: 'documentResolutionList',
    method: 'get',
    url: `/json/doc-type/document/review/resolution/byDocTypeDocument/${documentId}`,
  })
}

const documentResolutionCreate = (
  reviewId: number,
  credentials: DocumentResolutionCredentials
): Promise<DocumentResolution> => {
  return sendRequest({
    name: 'documentResolutionCreate',
    method: 'post',
    url: `/json/doc-type/document/review/resolution/create/${reviewId}`,
    payload: credentials,
  })
}

export {
  // document
  documentList,
  documentSingle,
  documentCreate,
  documentUpdate,
  documentDelete,
  documentRevision,
  documentSendToRegistration,
  documentManualRegister,
  documentRejectRegistration,
  documentReturnFromRegistration,
  documentCounts,
  documentDashboard,
  printedFormDocument,

  // route
  documentActiveRoute,
  documentRouteRun,

  // stage
  documentRouteStageCreate,
  documentRouteStageUpdate,
  documentRouteStageDelete,

  // participant
  documentRouteStageParticipantCreate,
  documentRouteStageParticipantDelete,
  documentRouteStageParticipantSetResult,

  // signatures
  documentSignatureList,
  documentSignatureCreate,

  // acceptance
  documentAcceptanceList,
  documentAcceptanceCreate,
  documentAcceptanceUpdate,
  documentAcceptanceDelete,
  documentAcceptanceConfirmationList,
  documentAcceptanceConfirmationCreate,
  documentAcceptanceStatus,

  // relation
  documentRelationList,
  documentRelationCreate,
  documentRelationDelete,

  // history
  documentHistoryList,

  // version
  documentVersionList,
  documentVersionRollback,

  // review
  documentReviewList,
  documentSendToReview,
  documentReturnFromReview,
  documentChangeReviewer,

  // resolution
  documentResolutionList,
  documentResolutionCreate,
}
